/**
 * VÉRAULT Portal — Content Studio · Campaigns
 *
 * Lightweight admin surface for editorial collections that thread through
 * journals, media, and social posts. Two-pane layout:
 *   · Left: list of campaigns (active + archived toggle)
 *   · Right: detail/editor pane (create + edit + delete)
 *
 * Each campaign has:
 *   · name + slug + description
 *   · color (free-text hex)
 *   · public_visible (when true, surfaces on the public site)
 *   · featured (boosts relevance in AI surfaces + public listings)
 *
 * Live counts of linked content come from /api/cms/campaigns/:id.
 */
import React from "react";
import {
  Plus,
  Tag,
  Trash2,
  EyeOff,
  Eye,
  Star,
  Archive,
  RotateCcw,
  Save,
  X,
} from "lucide-react";
import {
  Surface,
  EditorialRule,
  StatusTag,
  Empty,
  Skeleton,
  PortalButton,
  api,
} from "../../../index";

const PRESET_COLORS = [
  "#C8A85A", "#7A8A6C", "#B0644A", "#4A6A7A", "#A38FB0",
  "#7B6A4F", "#6E7E91", "#86766A", "#9A8E66", "#5B6B62",
];

function emptyDraft() {
  return {
    id: null,
    name: "",
    slug: "",
    description: "",
    color: "",
    public_visible: false,
    featured: false,
  };
}

export default function ContentCampaigns() {
  const [campaigns, setCampaigns] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState(null);
  const [draft, setDraft] = React.useState(null); // null = nothing being edited
  const [counts, setCounts] = React.useState({}); // {id: {journal_count, media_count, social_count}}
  const [saving, setSaving] = React.useState(false);

  const refresh = React.useCallback(() => {
    setCampaigns(null);
    api
      .get("/cms/campaigns", { params: { include_archived: includeArchived } })
      .then(({ data }) => setCampaigns(Array.isArray(data) ? data : []))
      .catch(() => setError("Couldn't load campaigns."));
  }, [includeArchived]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  React.useEffect(() => {
    if (!selectedId) {
      setDraft(null);
      return;
    }
    // Fetch the campaign + counts
    api
      .get(`/cms/campaigns/${selectedId}`)
      .then(({ data }) => {
        setDraft({
          id: data.id,
          name: data.name || "",
          slug: data.slug || "",
          description: data.description || "",
          color: data.color || "",
          public_visible: !!data.public_visible,
          featured: !!data.featured,
          archived: !!data.archived,
        });
        setCounts((c) => ({
          ...c,
          [data.id]: {
            journal_count: data.journal_count || 0,
            media_count: data.media_count || 0,
            social_count: data.social_count || 0,
          },
        }));
      })
      .catch(() => setDraft(null));
  }, [selectedId]);

  const handleNew = () => {
    setSelectedId(null);
    setDraft(emptyDraft());
  };

  const handleSave = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      window.alert("Campaign name is required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: draft.name.trim(),
        slug: draft.slug.trim(),
        description: draft.description.trim(),
        color: draft.color.trim(),
        public_visible: draft.public_visible,
        featured: draft.featured,
      };
      if (draft.id) {
        const r = await api.patch(`/cms/campaigns/${draft.id}`, body);
        setSelectedId(r.data.id);
      } else {
        const r = await api.post("/cms/campaigns", body);
        setSelectedId(r.data.id);
      }
      refresh();
    } catch (e) {
      window.alert(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (archive) => {
    if (!draft?.id) return;
    await api.patch(`/cms/campaigns/${draft.id}`, { archived: archive });
    setDraft({ ...draft, archived: archive });
    refresh();
  };

  const handleDelete = async () => {
    if (!draft?.id) return;
    const c = counts[draft.id];
    const linked = (c?.journal_count || 0) + (c?.media_count || 0) + (c?.social_count || 0);
    const msg = linked
      ? `Delete "${draft.name}"? ${linked} linked item${linked === 1 ? "" : "s"} will lose this campaign assignment. This cannot be undone.`
      : `Delete "${draft.name}"? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    await api.delete(`/cms/campaigns/${draft.id}`);
    setSelectedId(null);
    setDraft(null);
    refresh();
  };

  return (
    <div className="space-y-6" data-testid="content-campaigns">
      <div className="flex items-center justify-between gap-3">
        <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-secondary))] max-w-2xl">
          Group journals, media, and social posts into editorial collections. Mark a campaign{" "}
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Public</span>{" "}
          to surface it on the live site; mark it{" "}
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]">Featured</span>{" "}
          to nudge AI recommendations and public listings.
        </p>
        <PortalButton
          variant="primary"
          size="md"
          icon={<Plus className="size-3.5" strokeWidth={1.5} />}
          onClick={handleNew}
          data-testid="campaigns-new"
        >
          New campaign
        </PortalButton>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-6">
        {/* Left list */}
        <aside className="space-y-3" data-testid="campaigns-list">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              {campaigns ? `${campaigns.length} campaign${campaigns.length === 1 ? "" : "s"}` : "Loading"}
            </p>
            <button
              onClick={() => setIncludeArchived((v) => !v)}
              data-testid="campaigns-toggle-archived"
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors inline-flex items-center gap-1"
            >
              <Archive className="size-3" strokeWidth={1.5} />
              {includeArchived ? "Hide archived" : "Show archived"}
            </button>
          </div>

          {error ? (
            <Empty title={error} icon={<Tag className="size-5" strokeWidth={1.25} />} />
          ) : !campaigns ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : campaigns.length === 0 ? (
            <Empty
              title="No campaigns yet"
              hint="Create your first editorial collection."
              icon={<Tag className="size-5" strokeWidth={1.25} />}
              action={
                <PortalButton size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={handleNew}>
                  New campaign
                </PortalButton>
              }
            />
          ) : (
            <div className="space-y-1">
              {campaigns.map((c) => {
                const active = selectedId === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    data-testid={`campaigns-row-${c.id}`}
                    className={`w-full text-left px-3 py-2.5 rounded transition-colors flex items-center gap-2.5 ${
                      active
                        ? "bg-[hsl(var(--background-paper))]"
                        : "hover:bg-[hsl(var(--background-paper))]/60"
                    }`}
                  >
                    <span
                      className="size-2 rounded-full shrink-0"
                      style={{ background: c.color || "rgba(0,0,0,0.2)" }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className={`font-body text-[13px] truncate ${active ? "font-serif" : ""}`}>
                        {c.name}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] truncate">
                        {c.archived ? "Archived · " : ""}
                        {c.public_visible ? "Public" : "Internal"}
                        {c.featured && <span className="text-[hsl(var(--accent-gold))] ml-1">· featured</span>}
                      </div>
                    </div>
                    {c.featured && <Star className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />}
                  </button>
                );
              })}
            </div>
          )}
        </aside>

        {/* Detail pane */}
        <div className="min-w-0">
          {!draft ? (
            <Empty
              title="Pick a campaign to edit"
              hint="Or start a fresh one."
              icon={<Tag className="size-6" strokeWidth={1.25} />}
              action={
                <PortalButton size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={handleNew}>
                  New campaign
                </PortalButton>
              }
            />
          ) : (
            <Surface variant="default" padded className="space-y-6" data-testid="campaigns-editor">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                    {draft.id ? "Edit campaign" : "New campaign"}
                  </p>
                  <h3 className="mt-1 font-serif text-2xl text-[hsl(var(--foreground))]">
                    {draft.name || (draft.id ? "Untitled campaign" : "A new collection.")}
                  </h3>
                  {draft.archived && (
                    <StatusTag tone="neutral" className="mt-2">
                      Archived
                    </StatusTag>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedId(null);
                    setDraft(null);
                  }}
                  data-testid="campaigns-editor-close"
                  className="size-8 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                  aria-label="Close"
                >
                  <X className="size-4" strokeWidth={1.5} />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                    Name
                  </label>
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="e.g. Slow Travel"
                    data-testid="campaigns-editor-name"
                    className="mt-1.5 w-full h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                    Slug <span className="opacity-60">(auto if blank)</span>
                  </label>
                  <input
                    type="text"
                    value={draft.slug}
                    onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                    placeholder="slow-travel"
                    data-testid="campaigns-editor-slug"
                    className="mt-1.5 w-full h-10 px-3 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
                  />
                </div>
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                  Description
                </label>
                <textarea
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  rows={3}
                  placeholder="One calm sentence about what this collection is about."
                  data-testid="campaigns-editor-description"
                  className="mt-1.5 w-full px-3 py-2.5 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))] font-body leading-relaxed"
                />
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
                  Color
                </label>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setDraft({ ...draft, color: c })}
                      data-testid={`campaigns-color-${c.slice(1)}`}
                      title={c}
                      className={`size-7 rounded-full border-2 transition-transform ${
                        draft.color === c ? "scale-110 border-[hsl(var(--foreground))]" : "border-transparent hover:scale-105"
                      }`}
                      style={{ background: c }}
                    />
                  ))}
                  <input
                    type="text"
                    value={draft.color}
                    onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                    placeholder="#hex"
                    data-testid="campaigns-editor-color"
                    className="ml-1 h-8 w-24 px-2 text-[12px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))]"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[rgba(0,0,0,0.05)]">
                <button
                  onClick={() => setDraft({ ...draft, public_visible: !draft.public_visible })}
                  data-testid="campaigns-editor-public"
                  className={`inline-flex items-center gap-2 h-9 px-3 rounded-full text-[12.5px] tracking-tight border transition-colors ${
                    draft.public_visible
                      ? "bg-[hsl(var(--accent-sage))]/15 text-[hsl(var(--accent-sage))] border-[hsl(var(--accent-sage))]/30"
                      : "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))] border-[rgba(0,0,0,0.08)] hover:text-[hsl(var(--foreground))]"
                  }`}
                >
                  {draft.public_visible ? (
                    <Eye className="size-3.5" strokeWidth={1.5} />
                  ) : (
                    <EyeOff className="size-3.5" strokeWidth={1.5} />
                  )}
                  {draft.public_visible ? "Public" : "Internal"}
                </button>
                <button
                  onClick={() => setDraft({ ...draft, featured: !draft.featured })}
                  data-testid="campaigns-editor-featured"
                  className={`inline-flex items-center gap-2 h-9 px-3 rounded-full text-[12.5px] tracking-tight border transition-colors ${
                    draft.featured
                      ? "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] border-[hsl(var(--accent-gold))]/30"
                      : "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))] border-[rgba(0,0,0,0.08)] hover:text-[hsl(var(--foreground))]"
                  }`}
                >
                  <Star className="size-3.5" strokeWidth={1.5} />
                  {draft.featured ? "Featured" : "Standard"}
                </button>
              </div>

              {draft.id && counts[draft.id] && (
                <>
                  <EditorialRule label="Linked content" />
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: "Journals", value: counts[draft.id].journal_count },
                      { label: "Media", value: counts[draft.id].media_count },
                      { label: "Social", value: counts[draft.id].social_count },
                    ].map((m) => (
                      <div
                        key={m.label}
                        className="rounded border border-[rgba(0,0,0,0.06)] bg-[hsl(var(--background-paper))]/40 px-4 py-3"
                      >
                        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                          {m.label}
                        </div>
                        <div className="mt-1 font-serif text-xl text-[hsl(var(--foreground))]">
                          {m.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="flex items-center justify-between gap-3 pt-2">
                <div className="flex items-center gap-1.5">
                  {draft.id && (
                    <button
                      onClick={() => handleArchive(!draft.archived)}
                      data-testid="campaigns-editor-archive"
                      className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors px-2 py-1.5"
                    >
                      {draft.archived ? (
                        <>
                          <RotateCcw className="size-3" strokeWidth={1.5} /> Restore
                        </>
                      ) : (
                        <>
                          <Archive className="size-3" strokeWidth={1.5} /> Archive
                        </>
                      )}
                    </button>
                  )}
                  {draft.id && (
                    <button
                      onClick={handleDelete}
                      data-testid="campaigns-editor-delete"
                      className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))] transition-colors px-2 py-1.5"
                    >
                      <Trash2 className="size-3" strokeWidth={1.5} /> Delete
                    </button>
                  )}
                </div>
                <PortalButton
                  variant="primary"
                  size="md"
                  icon={<Save className="size-3.5" strokeWidth={1.5} />}
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="campaigns-editor-save"
                >
                  {saving ? "Saving…" : draft.id ? "Save changes" : "Create campaign"}
                </PortalButton>
              </div>
            </Surface>
          )}
        </div>
      </div>
    </div>
  );
}
